import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'

import PerfectScrollbar from 'react-perfect-scrollbar'
import DividerWithText from '@/modules/base/components/divider-with-text/divider-with-text'
import ListTagFilterInstance from './list-tag-filter-instance'
import ButtonSquare from '@/modules/base/components/buttons/square/button-square'
import { FONT_SIZES } from '@/theme'

import createTagFilterValues from './createTagFilterValues'

const CONTAINS = 'contains'

const initFilterModel = {
  filterType: 'multi',
  filterModels: [
    {
      filterType: 'text',
      operator: 'AND',
      type: CONTAINS, // 'NOT_CONTAINS'
      values: [],
    },
  ],
}

const getInitFilterModel = () => cloneDeep(initFilterModel)

const ListTagFilter = forwardRef((props, ref) => {
  const { filterChangedCallback, api } = props
  const classes = useStyles()

  const [filter, setFilter] = useState(getInitFilterModel())
  const [filterActive, setFilterActive] = useState(false)
  const [conditionArrays, setConditionArrays] = useState({ containsArray: [], notContainsArray: [] })

  const refComponent = useRef(null)
  const perfectScrollbarRef = useRef(null)
  const lastInstance = useRef(null)
  const hidePopupRef = useRef(null)

  const addCondition = () => {
    Promise.resolve(setFilter({
      ...filter,
      filterModels: [...filter.filterModels, { filterType: 'text', type: CONTAINS }],
    })).then(() => {
      initScrollbar()
      scrollToEnd()
    })
  }

  useEffect(() => {
    initScrollbar()
  }, [])

  useEffect(() => {
    const { filterModels } = filter || {}
    if (filterModels) {
      const containsArray = []
      const notContainsArray = []

      filterModels.forEach((item) => {
        if (item?.values?.length) {
          if (item.type === CONTAINS) {
            containsArray.push(item)
          } else {
            notContainsArray.push(item)
          }
        }
      })
      setConditionArrays({ containsArray, notContainsArray })
    }
  }, [filter?.filterModels])

  const initScrollbar = () => {
    if (perfectScrollbarRef?.current) {
      perfectScrollbarRef.current._ps.isScrollbarXUsingBottom = false
      perfectScrollbarRef.current._ps.isScrollbarYUsingRight = true
      perfectScrollbarRef.current._ps.scrollbarXBottom = 0
      perfectScrollbarRef.current._ps.scrollbarYRight = 0
      perfectScrollbarRef.current._ps.scrollbarYRail.style.left = ''
      perfectScrollbarRef.current._ps.scrollbarXRail.style.top = ''
    }
  }

  const scrollToEnd = () => {
    const ps = perfectScrollbarRef?.current
    if (ps?._ps?.contentHeight > 300 && lastInstance?.current) {
      lastInstance.current.scrollIntoView()
    }
  }

  const rowPassed = (tags) => {

    const { containsArray, notContainsArray } = conditionArrays
    const containsPassed = containsArray.every((containsItem) => {
      const { values } = containsItem
      return tags.some((tag) => {
        return values.some((filterCondition) => filterCondition === tag.label)
      })
    })

    const notContainsPassed = notContainsArray.every((notContainsItem) => {
      const { values } = notContainsItem
      return tags.every((tag) => {
        return values.every((filterCondition) => filterCondition !== tag.label)
      })
    })

    return (containsPassed === undefined ? true : containsPassed) && (notContainsPassed === undefined ? true : notContainsPassed)
  }

  useImperativeHandle(ref, () => {
    return {
      isFilterActive() {
        return filterActive
      },
      doesFilterPass(params) {
        const { node: { data: { tags } } } = params
        return rowPassed(tags)
      },
      getModel() {
        return filter
      },
      setModel(model) {
        if (model === null) {
          setFilterActive(false)
          setFilter(getInitFilterModel())
        } else {
          setFilter(model)
        }
      },
      afterGuiAttached(params) {
        hidePopupRef.current = params.hidePopup
      },
    }
  })

  const onSetFilter = () => {
    Promise.resolve(setFilterActive(true)).then(() => {
      filterChangedCallback()
      hidePopupRef.current && hidePopupRef.current()
    })
  }

  const onClearTagFilter = () => {
    setFilterActive(false)
    api.destroyFilter('tags', () => {
      filterChangedCallback()
    })
  }

  const onConditionChange = (index, newCondition) => {
    const newFilter = {
      ...filter,
      filterModels: filter.filterModels.map((item, mapIndex) => mapIndex === index ? newCondition : item),
    }
    setFilter(newFilter)
  }

  return (
    <div ref={refComponent} className={classes.tagFilterContainer}>

      <PerfectScrollbar
        component='div'
        ref={perfectScrollbarRef}
        className={classes.scrollbarContainer}
        options={{ suppressScrollX: true }}
      >
        {filter?.filterModels?.map((condition, index) => {
          return (
            <div className={classes.instanceContainer} key={`tag-filter-instance-${index}`}>
              <ListTagFilterInstance
                ref={(index === filter.filterModels?.length - 1) ? lastInstance : null}
                conditionIndex={index}
                tagFilterValues={createTagFilterValues(api)}
                condition={condition}
                onConditionChange={onConditionChange}
                conditionsCount={filter.filterModels?.length || 0}
                {...props}
              />
              {index < filter.filterModels?.length - 1 && <DividerWithText key={`divider-${index}`} text={lang.list.andCondition} />}
            </div>
          )
        })}
      </PerfectScrollbar>
      <div className={classes.buttonContainer}>
        <div className={clsx(classes.addCondition, 'icon icon-add')} onClick={addCondition}><span className={classes.addConditionText}>{lang.tag.addCondition}</span></div>
        <div className={classes.filterButtons}>
          <div className={classes.clearFilterButton} onClick={onClearTagFilter}>{lang.list.clearFilter}</div>
          <ButtonSquare customClass={classes.setFilterButton} label={lang.list.applyFilter} onClick={onSetFilter} />
        </div>
      </div>
    </div>
  )
})

const useStyles = makeStyles((theme) => ({
  tagFilterContainer: {
    width: 220,
    padding: '20px 0px 10px 20px',
  },
  scrollbarContainer: {
    height: '100%',
    maxHeight: 300,
    paddingRight: 20,
  },
  instanceContainer: {
    padding: '15px 0 0 0',
    '&:first-of-type': {
      padding: 0,
    },
  },
  buttonContainer: {
    paddingRight: 20,
  },
  filterButtons: {
    display: 'flex',
    marginTop: 10,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  setFilterButton: {
    width: 75,
    height: 30,
    fontSize: FONT_SIZES.smaller,
  },
  clearFilterButton: {
    color: theme.midGrey,
    fontSize: FONT_SIZES.smaller,
    '&:hover': {
      cursor: 'pointer',
      color: theme.darkestGrey,
    },
  },
  addCondition: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '5px 0 15px',
    color: theme.primary,
    cursor: 'pointer',
    fontSize: FONT_SIZES.smaller,
  },
  addConditionText: {
    marginLeft: '5px',
  },
}))

ListTagFilter.propTypes = {
  agGridReact: PropTypes.object,
  api: PropTypes.object,
  buttons: PropTypes.arrayOf(PropTypes.string),
  closeOnApply: PropTypes.bool,
  colDef: PropTypes.object,
  column: PropTypes.object,
  doesRowPassOtherFilter: PropTypes.func,
  filterChangedCallback: PropTypes.func,
  filterModifiedCallback: PropTypes.func,
  frameworkComponentWrapper: PropTypes.object,
  reactContainer: PropTypes.instanceOf(HTMLDivElement),
  rowModel: PropTypes.object,
  suppressMiniFilter: PropTypes.bool,
  valueGetter: PropTypes.func,
}

export default ListTagFilter
